/**
 * PersonaCard — first-person mirror of the visitor's *confirmed*
 * choices across the five journey stops (same as the Route Finder map).
 *
 * Renders as a series of short factual sentences. Each sentence drops
 * out silently if the source data is missing, so the card feels
 * coherent whether the student is 1/5 or 5/5 done. Curriculum and
 * grades show only when the student entered/confirmed them (how.grades.confirmed),
 * never from Quick Check's starting values.
 *
 *   • I am currently studying {curriculum}.
 *   • I am predicted {grades}.
 *   • I am leaning toward studying {subjects}.
 *   • I want to study in {cities}.
 *   • I have shortlisted {N} universities: {top N names}.
 *   • My next goal is to {…}.
 *
 * Variants:
 *   full     — the full card on `/journey`
 *   compact  — a one-liner strip for Landing / other pages
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useJourney } from "@/context/JourneyContext";
import { Sparkles, ArrowRight, Check } from "lucide-react";
import { buildPersona, PERSONA_STOPS, savedGradesToConfirm } from "@/lib/persona";
import { SavedGradesPrompt } from "@/components/SavedGradesPrompt";

export { buildPersona, PERSONA_STOPS };

export default function PersonaCard({ variant = "full" }) {
  const { profile } = useJourney();
  const { sentences, nextGoal, journeyComplete, gradesPending } = useMemo(
    () => buildPersona(profile || {}),
    [profile],
  );
  const completedSet = new Set(profile?.behaviour?.pillars_completed || []);
  const completedCount = PERSONA_STOPS.filter((st) => completedSet.has(st.progress)).length;
  const total = PERSONA_STOPS.length;
  const hasContent = sentences.length > 0 && sentences[0].key !== "empty";

  // Pulse-state for the pillar chips (full variant only). Kept at
  // the top so hooks run in the same order on every render — the
  // compact variant simply ignores this state.
  const [pulsePillar, setPulsePillar] = useState(null);
  useEffect(() => {
    let timer;
    const handler = (e) => {
      const key = e?.detail?.pillar;
      if (!key) return;
      setPulsePillar(key);
      clearTimeout(timer);
      timer = setTimeout(() => setPulsePillar(null), 1600);
    };
    window.addEventListener("wua:journey:pillar-completed", handler);
    return () => {
      window.removeEventListener("wua:journey:pillar-completed", handler);
      clearTimeout(timer);
    };
  }, []);

  if (variant === "compact") {
    // Show the journey progress + next-step CTA so the pill reads as
    // "here's where you are, click to continue" — not a floating quote.
    const nextStop = PERSONA_STOPS.find((st) => !completedSet.has(st.progress));
    const step = nextStop ? { label: nextStop.next, to: nextStop.to } : { label: "Review your shortlist", to: "/shortlist" };
    return (
      <Link
        to={step.to}
        data-testid="persona-strip"
        className="group inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50/70 pl-1 pr-3 py-1 text-[12px] text-violet-900 hover:bg-violet-100 hover:border-violet-300 transition-colors max-w-full"
      >
        <span
          className="inline-flex items-center gap-1 rounded-full bg-[#7C3AED] text-white px-2.5 py-0.5 text-[10px] font-mono font-semibold shrink-0"
          aria-label={`${completedCount} of ${total} stops confirmed`}
        >
          <Sparkles className="h-3 w-3" />
          {completedCount}/{total} confirmed
        </span>
        <span className="min-w-0 truncate">
          <span className="text-violet-700">Next:</span>{" "}
          <span className="font-semibold" data-testid="persona-strip-next">{step.label}</span>
        </span>
        <ArrowRight className="h-3 w-3 text-[#7C3AED] group-hover:translate-x-0.5 transition-transform shrink-0" />
      </Link>
    );
  }


  return (
    <div
      data-testid="persona-card"
      className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-white p-6 lg:p-7"
    >
      <style>{`
        @keyframes persona-chip-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(124,58,237,0.55); transform: scale(1); }
          50%  { box-shadow: 0 0 0 10px rgba(124,58,237,0);  transform: scale(1.08); }
          100% { box-shadow: 0 0 0 0 rgba(124,58,237,0);     transform: scale(1); }
        }
        .persona-chip-pulse { animation: persona-chip-pulse 1.6s ease-out; }
      `}</style>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-[#7C3AED]" />
        <span className="text-[10px] uppercase tracking-wider text-[#7C3AED] font-semibold">
          Your persona so far
        </span>
        <span className="text-[10px] text-stone-500">·</span>
        <span className="text-[10px] text-stone-500 font-mono" data-testid="persona-stops-count">{completedCount}/{total} stops confirmed</span>
      </div>

      {/* Pillar chip row — mirrors the tube map so the just-completed
          pillar can pulse when JourneyRail broadcasts a completion. */}
      <div
        className="flex flex-wrap items-center gap-2 mb-4"
        data-testid="persona-pillar-chips"
      >
        {PERSONA_STOPS.map((p, i) => {
          const done = completedSet.has(p.progress);
          const isPulsing = pulsePillar === p.key;
          return (
            <span
              key={p.key}
              data-testid={`persona-pillar-chip-${p.key}`}
              data-state={done ? "completed" : "pending"}
              className={[
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                done
                  ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                  : "bg-white text-stone-500 border-stone-300",
                isPulsing ? "persona-chip-pulse" : "",
              ].join(" ")}
            >
              {done ? (
                <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
              ) : (
                <span className="font-mono">{i + 1}</span>
              )}
              {p.label}
            </span>
          );
        })}
      </div>

      <ul className="space-y-1.5" data-testid="persona-sentences">
        {sentences.map((s) => (
          <li
            key={s.key}
            data-testid={`persona-sentence-${s.key}`}
            className="text-[15px] text-stone-800 leading-relaxed"
          >
            {s.text}
          </li>
        ))}
      </ul>
      <SavedGradesPrompt />
      {!savedGradesToConfirm(profile) && gradesPending && (
        <p className="mt-2 text-[13px] text-stone-600" data-testid="persona-grades-invite">
          Your curriculum and predicted grades aren't here until you add or confirm them.{" "}
          <Link to="/admission" data-testid="persona-grades-invite-link" className="underline decoration-2 underline-offset-2 text-[#4C1D95] hover:text-[#3B0764]">
            Add them in Quick Check
          </Link>
        </p>
      )}

      <div className="mt-4 pt-4 border-t border-violet-100">
        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1">
          {journeyComplete ? "Now for the fun part" : "Next"}
        </div>
        <p className="text-[15px] text-stone-900 font-medium" data-testid="persona-next-goal">
          {nextGoal}
        </p>

        {/* Journey-complete upsell — WUA+ turns "I know my destination"
            into a concrete "let's get your application landed" moment.
            Rendered only when every pillar is done so it's a reward
            after work, not an interruption. */}
        {journeyComplete && (
          <div
            data-testid="persona-wuaplus-cta"
            className="mt-4 rounded-2xl border border-[#4C1D95]/25 bg-gradient-to-br from-[#4C1D95] via-[#5b21b6] to-[#7C3AED] text-white p-5 sm:p-6 shadow-sm"
          >
            <div className="flex flex-wrap items-start gap-4">
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold">
                  <span aria-hidden="true">★</span> WUA+
                </div>
                <h3 className="mt-2 font-editorial text-xl sm:text-2xl leading-tight">
                  A human in your corner for the application push.
                </h3>
                <p className="mt-1.5 text-sm text-white/85">
                  Your Journey is set. Now a dedicated counsellor + Anna
                  AI turn it into personal statements, deadlines and
                  finance plans that actually get filed on time.
                </p>
                <ul className="mt-3 space-y-1 text-[13px] text-white/90">
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
                    Personal-statement critique against your 4 shortlisted courses
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
                    Family Wallet — every fee, scholarship and deadline in one place
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />
                    Up to 20 counsellor sessions on the Premium tier
                  </li>
                </ul>
              </div>
              <div className="w-full sm:w-auto flex flex-col gap-2 sm:items-end">
                <Link
                  to="/wua-plus?source=persona-complete"
                  data-testid="persona-wuaplus-primary-cta"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-[#4C1D95] font-semibold text-sm px-4 py-2 hover:bg-stone-100 transition-colors"
                >
                  Book a WUA+ intro call <span aria-hidden="true">→</span>
                </Link>
                <Link
                  to="/wua-plus/sample"
                  data-testid="persona-wuaplus-secondary-cta"
                  className="text-[11px] text-white/80 hover:text-white underline underline-offset-2 text-center sm:text-right"
                >
                  See a sample report first
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="mt-4 text-[11px] text-stone-500 italic">
        Sentences update automatically as you confirm your choices at the end of each stage. Reset a stage below to rewrite it.
      </p>
    </div>
  );
}
